import { Injectable } from '@angular/core';
import { catchError, pluck } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { APP_CONFIG } from './config.service';
import { HttpHeaders } from '@angular/common/http';

interface Address {
  country: string;
  postal_code: string;
  locality: string;
}

interface Timespan {
  begin: string;
  end: string;
}

interface Operation {
  operation_order: number;
  type: string;
  loads: LoadDetails[];
  timespans: Timespan;
}

export interface LoadDetails {
  load_id: string;
  name?: string;
  type_of_load?: string;
  amount?: number;
  weight: LoadsWeight;
  height?: number;
  width?: number;
  length?: number;
  is_stackable?: boolean;
}

export interface LoadsWeight {
  value: number;
  unit_code: string;
}

export interface TransSpotSchema {
  spot_order: number;
  place: {
    address: Address;
  };
  operations: Operation[];
}

interface Requirement {
  is_ftl: boolean;
  required_truck_bodies: string[];
  required_ways_of_loading
}

export interface TransRequest {
  publish: boolean;
  capacity: number;
  loads: LoadDetails[];
  loading_meters?: number;
  vehicle_size?: string[];
  carrier_id?: number;
  requirements: Requirement;
  spots: TransSpotSchema[];
  pdf: Blob;
  payment?: Payment;
  monitoring?: Monitoring;
}

export interface Payment {
  days: number;
  price: { value: number; type: 'km' | 'route'; currency: string };
}

export interface Monitoring {
  required: boolean;
}

export interface ContractorAddress {
  country: string;
  locality: string;
  number: string | null;
  postal_code: string;
  street: string;
}

export interface Permissions {
  is_authorized: boolean;
  is_blocked: boolean;
  was_authorized_at_least_once: boolean;
}

export interface CompanyInfo {
  legal_name: string;
  vat_id: string;
}

export interface Contractor {
  company: {
    address: ContractorAddress;
    certificates: any[];
    email: string;
    fax: string;
    financial_verification_status: string | null;
    flags: any[];
    id: number;
    name: string;
    permissions: Permissions;
    roles: string[];
    status: string;
    telephone: string;
    type: string;
    vat_id: string;
  };
  groups: any[];
  id: string;
  notes: any[];
}

export interface ContractorsList {
  page: number;
  page_count: number;
  page_size: number;
  partners: Contractor[];
}

const httpOptions = {
  headers: new HttpHeaders({
    'X-Skip-Interceptor': 'true',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class TransService {
  private url: string;

  constructor(private http: HttpClient) {
    APP_CONFIG()
      .pipe(pluck('baseUri'))
      .subscribe((url: string) => (this.url = url));
  }

  public getCompanyInfo(isCatchError = true): Observable<any> {
    const request: Observable<any> = this.http.get(this.url + `trans/company/info`, httpOptions);
    return isCatchError
      ? request.pipe(catchError((error: HttpErrorResponse) => this.instantHandleError(error)))
      : request;
  }

public getContractorsList(isCatchError = true, page: number = null): Observable<any> {
  const url = page !== null ? `${this.url}trans/contractor/all?page=${page}` : `${this.url}trans/contractor/all`;
  const request: Observable<any> = this.http.get(url, httpOptions);
  return isCatchError
    ? request.pipe(catchError((error: HttpErrorResponse) => this.instantHandleError(error)))
    : request;
}

  public postNewFreightOffer(data: TransRequest, isCatchError = true): Observable<any> {
    const request: Observable<any> = this.http.post(
      this.url + `trans/freight/new`,
      data,
      httpOptions,
    );
    return isCatchError
      ? request.pipe(catchError((error: HttpErrorResponse) => this.instantHandleError(error)))
      : request;
  }

  public postNewOrder(data: TransRequest, isCatchError = true): Observable<any> {
    const request: Observable<any> = this.http.post(
      this.url + `trans/order/new`,
      data,
      httpOptions,
    );
    return isCatchError
      ? request.pipe(catchError((error: HttpErrorResponse) => this.instantHandleError(error)))
      : request;
  }

  private instantHandleError(error: HttpErrorResponse): Observable<any> {
    return throwError(error);
  }
}
